/**
 * PATH: packages/ui/src/components/settings/sender-profiles/PersonContextPanel.tsx
 * FILE_ID: UI-SETTINGS-PERSONCONTEXT-029
 * PURPOSE: W3-V8 cross-nesting panel — the PEOPLE behind a sender
 *          domain: each resolved person with their addresses, roles,
 *          and email/document/job footprint.
 * WHY: Golden records answer "who IS this?"; this panel is where the
 *      operator finally sees a person's whole footprint, not
 *      per-address fragments.
 * LANDMINE: lazy — fetches only on mount (the section mounts it only
 *      for the expanded domain). Renders a quiet empty state, never
 *      an error wall: context is optional, the row must stay usable.
 * BACKEND: quotech-backend email_kanban/views/persons.py (cross-repo).
 * RELATED: UI-SETTINGS-SENDERS-027 (mounts this under an expanded row).
 */

import { useEffect, useState } from 'react'
import { Briefcase, FileText, Loader2, Mail } from 'lucide-react'
import type { SettingsApiClient } from '../types'
import type { PersonOverview } from './types'
import { fetchPersonOverviews } from './senderProfilesApi'

const ROLE_LABEL: Record<string, string> = {
  builder_client: 'Client',
  supplier: 'Supplier',
  subcontractor: 'Subcontractor',
  internal_team: 'Internal',
  consultant: 'Consultant',
  authority: 'Authority',
}

function Footprint({ person }: { person: PersonOverview }) {
  return (
    <div className="flex items-center gap-3 text-[11px] text-slate-500 dark:text-slate-400">
      <span className="inline-flex items-center gap-1">
        <Mail className="w-3 h-3" /> {person.emails.count}
      </span>
      <span className="inline-flex items-center gap-1">
        <FileText className="w-3 h-3" /> {person.documents.count}
      </span>
      {person.jobs.length > 0 && (
        <span
          className="inline-flex items-center gap-1 truncate"
          title={person.jobs.map((j) => `${j.label} (${j.email_count})`).join(', ')}
        >
          <Briefcase className="w-3 h-3" />
          {person.jobs.slice(0, 2).map((j) => j.label).join(', ')}
          {person.jobs.length > 2 ? ` +${person.jobs.length - 2}` : ''}
        </span>
      )}
    </div>
  )
}

export interface PersonContextPanelProps {
  apiClient: SettingsApiClient
  domain: string
}

export function PersonContextPanel({ apiClient, domain }: PersonContextPanelProps) {
  const [persons, setPersons] = useState<PersonOverview[]>([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    let alive = true
    setLoading(true)
    fetchPersonOverviews(apiClient, { domain })
      .then((data) => {
        if (alive) setPersons(data.persons)
      })
      .catch(() => {
        if (alive) setPersons([])
      })
      .finally(() => {
        if (alive) setLoading(false)
      })
    return () => {
      alive = false
    }
  }, [apiClient, domain])

  if (loading) {
    return (
      <div className="flex items-center gap-2 text-xs text-slate-400 px-3 py-2.5">
        <Loader2 className="w-3.5 h-3.5 animate-spin" /> Loading people…
      </div>
    )
  }
  if (persons.length === 0) {
    return (
      <p className="text-xs text-slate-400 px-3 py-2.5">
        No resolved people at this domain yet. They appear as the nightly
        contact resolution links senders to contacts.
      </p>
    )
  }

  return (
    <div className="divide-y divide-slate-100 dark:divide-slate-800 bg-slate-50/60 dark:bg-slate-900/40 rounded-md">
      {persons.map((p) => (
        <div key={p.person.id} className="px-3 py-2">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-xs font-medium text-slate-800 dark:text-slate-100">
              {p.person.display_name || p.person.primary_email || 'Unnamed contact'}
            </span>
            {p.roles.slice(0, 3).map((r) => (
              <span
                key={r.role}
                title={`${ROLE_LABEL[r.role] || r.role}: ${Math.round((r.confidence || 0) * 100)}% via ${r.evidence_source}`}
                className="rounded-full border border-slate-200 dark:border-slate-700 px-1.5 py-0.5 text-[10px] text-slate-500 dark:text-slate-400"
              >
                {ROLE_LABEL[r.role] || r.role}
              </span>
            ))}
          </div>
          {p.addresses.length > 1 && (
            <p className="text-[11px] text-slate-400 truncate mt-0.5">
              {p.addresses.join(', ')}
            </p>
          )}
          <div className="mt-1">
            <Footprint person={p} />
          </div>
        </div>
      ))}
    </div>
  )
}

export default PersonContextPanel
